'use client';

import useEmblaCarousel from 'embla-carousel-react';
import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';

import { AppImage } from '@/app/components/media/app-image';
import { usePaginationSlider } from '@/app/components/sliders/hooks/usePaginationSlider';
import { usePrevNextSlider } from '@/app/components/sliders/hooks/usePrevNextSlider';
import type { SpartanProductAccessoriesCardsCarouselProps } from '@/app/lcv/components/sections/spartan/products/accessories/cards-carousel/types';
import {
  SPARTAN_PRODUCT_ACCESSORIES_DATA_GA,
  SPARTAN_PRODUCT_ACCESSORIES_TRACKING,
} from '@/app/lcv/components/sections/spartan/products/accessories/tracking';
import exploreFeaturesCarouselStyles from '@/app/lcv/components/sections/spartan/products/explore-features/carousel/styles.module.css';
import { SPARTAN_PRODUCT_ICON_BUTTON_CLASS_NAME } from '@/app/lcv/components/sections/spartan/products/shared/icon-button';
import { SPARTAN_PRODUCT_LIST_GAP_CLASS_NAME } from '@/app/lcv/components/sections/spartan/products/shared/list-gap';
import {
  getSpartanRevealElementProps,
  SPARTAN_REVEAL_IMMEDIATE_SCROLL_OFFSET,
  SPARTAN_REVEAL_PRESET,
  SPARTAN_REVEAL_ROLE,
} from '@/app/lcv/components/sections/spartan/products/shared/reveal';
import { SPARTAN_PRODUCTS_EVENT_NAME } from '@/app/lcv/components/sections/spartan/products/shared/tracking';
import { useCarouselImageLoading } from '@/app/lcv/components/sections/spartan/shared/hooks/useCarouselImageLoading';
import { SpartanSlidePagination } from '@/app/lcv/components/sections/spartan/shared/slide-pagination';
import { SpartanSliderNav } from '@/app/lcv/components/sections/spartan/shared/slider-nav';
import { SPARTAN_SLIDER_NAV_LABELS } from '@/app/lcv/components/sections/spartan/shared/slider-nav/constants';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { stripHtmlTags } from '@/app/utils/format';

const SpartanProductAccessoriesRemarkNote = dynamic(() =>
  import('@/app/lcv/components/sections/spartan/products/accessories/remark').then(
    (mod) => mod.SpartanProductAccessoriesRemarkNote
  )
);

/**
 * Equal card height per breakpoint; width follows each image aspect ratio.
 * Mobile height ≈ content width with peek so 1:1 cards nearly fill the row.
 */
const ACCESSORIES_CARD_HEIGHT_DESKTOP_PX = 480;

export const SpartanProductAccessoriesCardsCarousel = ({
  features,
  remark,
}: SpartanProductAccessoriesCardsCarouselProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'start',
    dragFree: true,
    duration: 32,
  });
  const { currentPage, onPageChange, scrollSnaps } =
    usePaginationSlider(emblaApi);
  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextSlider(emblaApi);
  const [canScroll, setCanScroll] = useState<boolean>(false);
  const { getImageLoading, setViewportRef } = useCarouselImageLoading({
    emblaApi,
    emblaRef,
    itemCount: features.length,
  });

  useEffect(() => {
    if (!emblaApi) return;

    const handleUpdateCanScroll = () => {
      setCanScroll(emblaApi.scrollSnapList().length > 1);
    };

    handleUpdateCanScroll();
    emblaApi.on('reInit', handleUpdateCanScroll);

    return () => {
      emblaApi.off('reInit', handleUpdateCanScroll);
    };
  }, [emblaApi]);

  return (
    <>
      <div
        ref={setViewportRef}
        role="region"
        aria-roledescription="carousel"
        aria-label={SPARTAN_SLIDER_NAV_LABELS.carousel}
        className="[--spartan-accessories-card-height:min(400px,calc(100vw-48px))] lg:[--spartan-accessories-card-height:480px]"
      >
        <ul
          className={cn(
            'where-span:text-inline-wrap where-small:text-body-2-fixed flex items-start',
            SPARTAN_PRODUCT_LIST_GAP_CLASS_NAME
          )}
          {...createDataAttributes({
            test: SPARTAN_PRODUCT_ACCESSORIES_TRACKING.cardList,
          })}
        >
          {features.map((feature, index) => {
            const aspectRatio = feature.image.width / feature.image.height;
            const desktopCardWidthPx = Math.round(
              ACCESSORIES_CARD_HEIGHT_DESKTOP_PX * aspectRatio
            );

            return (
              <li
                key={feature.id ?? [feature.title, index].join('-')}
                className="flex shrink-0 grow-0 flex-col overflow-hidden"
                {...getSpartanRevealElementProps({
                  order: Math.min(index, 3),
                  preset: SPARTAN_REVEAL_PRESET.collection,
                  role: SPARTAN_REVEAL_ROLE.exploreCard,
                  staggerMs: 60,
                })}
              >
                <div
                  className="relative overflow-hidden rounded-lg text-left"
                  {...createDataAttributes({
                    test: SPARTAN_PRODUCT_ACCESSORIES_TRACKING.card(index),
                  })}
                >
                  <AppImage
                    src={feature.image.src}
                    alt={stripHtmlTags(feature.title)}
                    width={feature.image.width}
                    height={feature.image.height}
                    customRatio
                    className="h-(--spartan-accessories-card-height) w-auto"
                    imgClassName={exploreFeaturesCarouselStyles.image}
                    sizes={`(min-width: 1024px) ${desktopCardWidthPx}px, calc(min(400px, 100vw - 48px) * ${aspectRatio})`}
                    testId={SPARTAN_PRODUCT_ACCESSORIES_TRACKING.cardImage(
                      index
                    )}
                    {...getImageLoading(index)}
                  />
                  <div
                    aria-hidden
                    className="absolute inset-x-0 bottom-0 h-1/2 bg-linear-to-t from-black/60 to-transparent"
                  />
                  <div className="absolute inset-x-0 bottom-0 space-y-2 p-4 text-white md:p-6">
                    {!!feature.eyebrow && (
                      <p
                        className="text-sub-h2 font-noto-bold text-white"
                        dangerouslySetInnerHTML={{ __html: feature.eyebrow }}
                        {...createDataAttributes({
                          test: SPARTAN_PRODUCT_ACCESSORIES_TRACKING.cardEyebrow(
                            index
                          ),
                        })}
                      />
                    )}
                    <h3
                      className="font-noto-bold text-body-1"
                      dangerouslySetInnerHTML={{ __html: feature.title }}
                      {...createDataAttributes({
                        test: SPARTAN_PRODUCT_ACCESSORIES_TRACKING.cardTitle(
                          index
                        ),
                      })}
                    />
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
      {!!canScroll && (
        <div
          {...getSpartanRevealElementProps({
            preset: SPARTAN_REVEAL_PRESET.content,
            role: SPARTAN_REVEAL_ROLE.fade,
            scrollOffset: SPARTAN_REVEAL_IMMEDIATE_SCROLL_OFFSET,
          })}
        >
          <SpartanSlidePagination
            theme="dark"
            currentPage={currentPage}
            totalPages={scrollSnaps.length}
            onPageChange={onPageChange}
            wrapperClassName="mt-6 justify-center md:mt-8 lg:mt-10 lg:hidden"
            tracking={{
              eventName: SPARTAN_PRODUCTS_EVENT_NAME,
              group: SPARTAN_PRODUCT_ACCESSORIES_DATA_GA.section,
            }}
          />
        </div>
      )}
      {(!!remark || !!canScroll) && (
        <div
          className={cn('mt-6 flex items-center gap-4 md:gap-6', {
            'md:mt-8 lg:mt-10': canScroll,
          })}
          {...getSpartanRevealElementProps({
            preset: SPARTAN_REVEAL_PRESET.content,
            role: SPARTAN_REVEAL_ROLE.copy,
            scrollOffset: SPARTAN_REVEAL_IMMEDIATE_SCROLL_OFFSET,
          })}
        >
          {!!remark && (
            <SpartanProductAccessoriesRemarkNote
              remark={remark}
              className="min-w-0 flex-1"
            />
          )}
          {!!canScroll && (
            <SpartanSliderNav
              onPrev={onPrevButtonClick}
              onNext={onNextButtonClick}
              isPrevDisabled={prevBtnDisabled}
              isNextDisabled={nextBtnDisabled}
              className="ml-auto hidden shrink-0 justify-center lg:flex"
              buttonClassName={SPARTAN_PRODUCT_ICON_BUTTON_CLASS_NAME}
              tracking={{
                eventName: SPARTAN_PRODUCTS_EVENT_NAME,
                group: SPARTAN_PRODUCT_ACCESSORIES_DATA_GA.section,
              }}
            />
          )}
        </div>
      )}
    </>
  );
};
